import { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useTransform } from "motion/react";
// replace icons with your own if needed
import {
	BsPersonRaisedHand,
	BsFillShieldFill,
	BsFillPeopleFill,
} from "react-icons/bs";
import PropTypes from "prop-types";

const DEFAULT_ITEMS = [
	{
		title: "Individual Lessons",
		description:
			"Personalized driving lessons in Canberra to match your pace and needs. Learn at your own comfort level with expert guidance, ensuring a smooth and confident driving experience.",
		icon: <BsPersonRaisedHand size={20} color="#FF9A00" />,
		id: 1,
	},
	{
		title: "Confidence Building",
		description:
			"We help nervous drivers build confidence through patient and supportive instruction.",
		icon: <BsFillShieldFill size={20} color="#FF9A00" />,
		id: 2,
	},
	{
		title: "Diverse Experience",
		description:
			"With experience in teaching students of all backgrounds and skill levels, our instructors know how to effectively communicate and instruct.",
		icon: <BsFillPeopleFill size={20} color="#FF9A00" />,
		id: 3,
	},
];

const DRAG_BUFFER = 0;
const VELOCITY_THRESHOLD = 500;
const GAP = 16;
const SPRING_OPTIONS = { type: "spring", stiffness: 300, damping: 30 };

export default function Carousel({
	items = DEFAULT_ITEMS,
	baseWidth = 500,
	autoplay = false,
	autoplayDelay = 3000,
	pauseOnHover = false,
	loop = false,
	round = false,
}) {
	const containerRef = useRef(null);
	const [containerWidth, setContainerWidth] = useState(baseWidth);

	useEffect(() => {
		if (containerRef.current) {
			const resizeObserver = new ResizeObserver((entries) => {
				for (let entry of entries) {
					setContainerWidth(entry.contentRect.width);
				}
			});

			resizeObserver.observe(containerRef.current);
			return () => resizeObserver.disconnect();
		}
	}, []);

	const containerPadding = 16;
	const itemWidth = containerWidth - containerPadding * 2;
	const trackItemOffset = itemWidth + GAP;

	const carouselItems = loop ? [...items, items[0]] : items;
	const [currentIndex, setCurrentIndex] = useState(0);
	const x = useMotionValue(0);
	const [isHovered, setIsHovered] = useState(false);
	const [isResetting, setIsResetting] = useState(false);

	useEffect(() => {
		if (pauseOnHover && containerRef.current) {
			const container = containerRef.current;
			const handleMouseEnter = () => setIsHovered(true);
			const handleMouseLeave = () => setIsHovered(false);
			container.addEventListener("mouseenter", handleMouseEnter);
			container.addEventListener("mouseleave", handleMouseLeave);
			return () => {
				container.removeEventListener("mouseenter", handleMouseEnter);
				container.removeEventListener("mouseleave", handleMouseLeave);
			};
		}
	}, [pauseOnHover]);

	useEffect(() => {
		if (autoplay && (!pauseOnHover || !isHovered)) {
			const timer = setInterval(() => {
				setCurrentIndex((prev) => {
					if (prev === items.length - 1 && loop) {
						return prev + 1;
					}
					if (prev === carouselItems.length - 1) {
						return loop ? 0 : prev;
					}
					return prev + 1;
				});
			}, autoplayDelay);
			return () => clearInterval(timer);
		}
	}, [
		autoplay,
		autoplayDelay,
		isHovered,
		loop,
		items.length,
		carouselItems.length,
		pauseOnHover,
	]);

	const effectiveTransition = isResetting ? { duration: 0 } : SPRING_OPTIONS;

	const handleAnimationComplete = () => {
		if (loop && currentIndex === carouselItems.length - 1) {
			setIsResetting(true);
			x.set(0);
			setCurrentIndex(0);
			setTimeout(() => setIsResetting(false), 50);
		}
	};

	const handleDragEnd = (_, info) => {
		const offset = info.offset.x;
		const velocity = info.velocity.x;
		if (offset < -DRAG_BUFFER || velocity < -VELOCITY_THRESHOLD) {
			if (loop && currentIndex === items.length - 1) {
				setCurrentIndex(currentIndex + 1);
			} else {
				setCurrentIndex((prev) => Math.min(prev + 1, carouselItems.length - 1));
			}
		} else if (offset > DRAG_BUFFER || velocity > VELOCITY_THRESHOLD) {
			if (loop && currentIndex === 0) {
				setCurrentIndex(items.length - 1);
			} else {
				setCurrentIndex((prev) => Math.max(prev - 1, 0));
			}
		}
	};

	const dragProps = loop
		? {}
		: {
				dragConstraints: {
					left: -trackItemOffset * (carouselItems.length - 1),
					right: 0,
				},
		  };

	return (
		<div
			ref={containerRef}
			className={`relative overflow-hidden p-2 w-full ${
				round ? "rounded-full border border-steerBg" : "rounded-xl bg-steerBg"
			}`}
		>
			<motion.div
				className="flex"
				drag="x"
				{...dragProps}
				style={{
					width: itemWidth,
					gap: `${GAP}px`,
					perspective: 1000,
					perspectiveOrigin: `${
						currentIndex * trackItemOffset + itemWidth / 2
					}px 50%`,
					x,
				}}
				onDragEnd={handleDragEnd}
				animate={{ x: -(currentIndex * trackItemOffset) }}
				transition={effectiveTransition}
				onAnimationComplete={handleAnimationComplete}
			>
				{carouselItems.map((item, index) => {
					const range = [
						-(index + 1) * trackItemOffset,
						-index * trackItemOffset,
						-(index - 1) * trackItemOffset,
					];
					const outputRange = [90, 0, -90];
					// eslint-disable-next-line react-hooks/rules-of-hooks
					const rotateY = useTransform(x, range, outputRange, { clamp: false });
					return (
						<motion.div
							key={index}
							className={`relative shrink-0 text-steerBlack flex flex-col ${
								round
									? "items-center justify-center text-center bg-[#060010] border-0"
									: "items-start justify-between bg-steerBg rounded-2xl"
							} overflow-hidden cursor-grab active:cursor-grabbing`}
							style={{
								width: itemWidth,
								height: round ? itemWidth : "100%",
								rotateY: rotateY,
								...(round && { borderRadius: "50%" }),
							}}
							transition={effectiveTransition}
						>
							<div
								className={` flex gap-4 items-center ${
									round ? "p-0 m-0" : "p-2"
								}`}
							>
								<span className="flex p-4 items-center justify-center rounded-full bg-white/60 backdrop-blur-md border border-steerBlack/20">
									{item.icon}
								</span>
								<div className="font-black text-lg">{item.title}</div>
							</div>
							<div className="p-2">
								<p className="text-sm">{item.description}</p>
							</div>
						</motion.div>
					);
				})}
			</motion.div>
			<div
				className={`flex w-full justify-center ${
					round ? "absolute z-20 bottom-12 left-1/2 -translate-x-1/2" : ""
				}`}
			>
				<div className="mt-2 flex w-[150px] justify-between px-8">
					{items.map((_, index) => (
						<motion.div
							key={index}
							className={`h-2 w-2 rounded-full cursor-pointer transition-colors duration-150 ${
								currentIndex % items.length === index
									? round
										? "bg-steerBlack"
										: "bg-steerBlack"
									: round
									? "bg-[#555]"
									: "bg-[rgba(51,51,51,0.4)]"
							}`}
							animate={{
								scale: currentIndex % items.length === index ? 1.2 : 1,
							}}
							onClick={() => setCurrentIndex(index)}
							transition={{ duration: 0.15 }}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

Carousel.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			description: PropTypes.string.isRequired,
			id: PropTypes.number.isRequired,
			icon: PropTypes.element.isRequired,
		})
	),
	baseWidth: PropTypes.number,
	autoplay: PropTypes.bool,
	autoplayDelay: PropTypes.number,
	pauseOnHover: PropTypes.bool,
	loop: PropTypes.bool,
	round: PropTypes.bool,
};
